import { inject, Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { TranslateLoader } from "@ngx-translate/core";
import { getApp } from "firebase/app";
import { doc, getDocFromCache, getFirestore, loadBundle } from "firebase/firestore";
import * as JSZip from "jszip";
import { from, Observable, of } from "rxjs";
import { catchError, shareReplay, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable } from "../app.constants";
import { Language, allLanguageCodes } from "@interfaces/translation-interface";
import { LoggingService } from "./logging.service";

@Injectable({
  providedIn: "root",
})
export class SalesFirestoreService implements TranslateLoader {
  protected loggingService = inject(LoggingService);

  getTranslation(lang): Observable<Data | null> {
    let selectedLang = "en";
  
    if (lang && typeof lang === "object") {
      selectedLang = typeof lang["language_code"] === 'string' ? lang["language_code"] : 'en';
    } else if (lang && typeof lang === "string") {
      selectedLang = lang;
    }
  
    if (selectedLang.includes("lan")) {
      selectedLang = "en";
    }
    
    if (!allLanguageCodes.includes(selectedLang as Language)) selectedLang = "en";
    const cached = CommonVariable.salesTranslation$?.value;
    if (cached?.lang === selectedLang && cached?.data && Object.keys(cached.data).length) {
      return of(cached.data);
    }
    return this.returnTranslatedData(selectedLang).pipe(
      catchError((err) => {
        this.loggingService.logError(`[getTranslation] Error loading translation for "${selectedLang}":`, err);
        return of({});
      })
    );
  }
  

  // Method to use firebase bundle to fetch translated data
  private async fetchTranslatedData(lang: string, retryCase?: boolean): Promise<Data> {
    try {
      const cdnUrl = environment.salesTranslationCDNUrl?.[lang];
      let bundleText: string | null = null;

      // Try CDN first
      if (cdnUrl) {
        try {
          const bundle = await fetch(cdnUrl);
          if (bundle?.ok) {
            bundleText = await bundle.text();
          } else {
            this.loggingService.logError(`[fetchTranslatedData] CDN response not ok (${bundle?.status}) for "${lang}"`);
          }
        } catch (cdnError) {
          this.loggingService.logError(`[fetchTranslatedData] CDN request failed for "${lang}":`, cdnError);
        }
      }

      // If CDN failed, try remote bundle
      if (!bundleText) {
        try {
          bundleText = await this.getRemoteBundle(lang);
        } catch (remoteError) {
          this.loggingService.logError(`[fetchTranslatedData] Remote bundle error for "${lang}":`, remoteError);
        }
      }

      // If we have bundle text, load it into Firestore
      if (bundleText) {
        try {
          const app = getApp();
          const db = getFirestore(app);
          if (db) {
            await loadBundle(db, bundleText);
            const ref = doc(db, "translations_sales_journey/" + lang);
            const snapshot = await getDocFromCache(ref);
            const data = snapshot.data();

            if (data && Object.keys(data).length > 0) {
              return data;
            } else {
              this.loggingService.logError(`[fetchTranslatedData] Bundle loaded but no valid data found for "${lang}"`);
            }
          } else {
            this.loggingService.logError(`[fetchTranslatedData] Firestore DB not available for "${lang}"`);
          }
        } catch (bundleError) {
          this.loggingService.logError(`[fetchTranslatedData] Bundle loading error for "${lang}":`, bundleError);
        }
      }

      // Retry once if this is the first attempt
      if (!retryCase) {
        this.loggingService.logError(`[fetchTranslatedData] Retrying fetch for "${lang}" (second attempt)...`);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.fetchTranslatedData(lang, true);
      }

      // Final fallback - return empty object
      this.loggingService.logError(`[fetchTranslatedData] All attempts failed for "${lang}", returning empty object`);
      return {};
    } catch (error) {
      if (!retryCase) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.fetchTranslatedData(lang, true);
      }

      return {};
    }
  }

  private returnTranslatedData(lang: string): Observable<Data> {
    return from(this.fetchTranslatedData(lang)).pipe(
      switchMap((translatedData) => {
        if (translatedData) {
          CommonVariable.salesTranslation$.next({ lang: lang, data: translatedData });
          return of(translatedData);
        } else {
          return of(null);
        }
      }),
      catchError(() => {
        return of(null);
      }),
      shareReplay(1)
    );
  }

  async getLocalBundle(lang) {
    try {
      const zipUrl = {
        de: "../../assets/sales-translates-zips/bundles_sales_translates_de.zip",
        en: "../../assets/sales-translates-zips/bundles_sales_translates_en.zip",
        es: "../../assets/sales-translates-zips/bundles_sales_translates_es.zip",
        fr: "../../assets/sales-translates-zips/bundles_sales_translates_fr.zip",
        it: "../../assets/sales-translates-zips/bundles_sales_translates_it.zip",
        hi: "../../assets/sales-translates-zips/bundles_translations_sales_journey_hi.zip",
        pt: "../../assets/sales-translates-zips/bundles_translations_sales_journey_pt.zip",
      }
      const bundleText = await this.extractZipData(zipUrl?.[lang], lang);
      return bundleText;
    } catch (error) {
      this.loggingService.logError(`[getLocalBundle] Error loading local bundle for "${lang}":`, error);
      return null;
    }
  }

  async extractZipData(zipUrl, lang) {
    try {
      if (!zipUrl) {
        this.loggingService.logError(`[extractZipData] No ZIP URL provided for "${lang}"`);
        return null;
      }

      const response = await fetch(zipUrl);
      if (!response?.ok) {
        this.loggingService.logError(`[extractZipData] ZIP not found at "${zipUrl}" (${response?.status}) for "${lang}"`);
        return null;
      }

      const data = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(data);
      const bundleFile = zip.file("bundleSalesData.txt");

      if (!bundleFile) {
        this.loggingService.logError(`[extractZipData] bundleSalesData.txt not found in ZIP for "${lang}"`);
        return null;
      }

      const textFile = await bundleFile.async("string");
      return textFile;
    } catch (error) {
      return null;
    }
  }

  async getRemoteBundle(lang, retryCase?: boolean) {
    const zipUrl = environment.salesTranslationRemoteBundleUrl;

    try {
      const bundleText = await this.extractZipData(zipUrl, lang);
      if (!bundleText) {
        if (!retryCase) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return this.getRemoteBundle(lang, true);
        } else {
          this.loggingService.logError(`[getRemoteBundle] Remote bundle failed after retry, trying local bundle for "${lang}"`);
          return await this.getLocalBundle(lang);
        }
      }
      return bundleText;
    } catch (error) {
      if (!retryCase) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return this.getRemoteBundle(lang, true);
      } else {
        this.loggingService.logError(`[getRemoteBundle] Falling back to local bundle after retry for "${lang}"`);
        return await this.getLocalBundle(lang);
      }
    }
  }
}
